import { useMemo, useState } from 'react'
import AppLayout from '../../components/layout/AppLayout'
import ReplenishmentMatrix from '../../components/replenishment/ReplenishmentMatrix'
import MobileReplenishmentList from '../../components/replenishment/mobile/MobileReplenishmentList'
import PainelKanban from '../../components/replenishment/mobile/PainelKanban'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useReplenishment } from '../../hooks/useReplenishment'
import { HIDES_ZERADO, isRupturaAcionavel, PRIORITY_BADGE, PRIORITY_ORDER, STATUS_BADGE } from '../../lib/replenishment'
import type { KanbanStatusOp, ProductRow, ReplenishmentPriority } from '../../types'

const STATUS_CHIPS: KanbanStatusOp[] = [
  'Compra Aprovada',
  'Ruptura no Fornecedor',
  'Consignado',
  'Bloqueado',
]

// Bucket de prioridade "Zerado" (saldo 0 sem motivo operacional). Usa a mesma
// lista de exclusão da régua de ruptura canônica (HIDES_ZERADO).
function isEffectiveZerado(r: ProductRow): boolean {
  return r.priority === 'Zerado' && !HIDES_ZERADO.has(r.kanban_status)
}

export default function ReplenishmentPage() {
  const isMobile = useIsMobile()
  const { rows, loading, error, hasFiliais, filialMap } = useReplenishment()

  const [search, setSearch] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<Set<KanbanStatusOp>>(new Set())
  const [priorityFilter, setPriorityFilter] = useState<Set<ReplenishmentPriority>>(new Set())
  const [encalheOnly, setEncalheOnly] = useState(false)
  // Painel K mobile: alterna entre a lista rica (filtros) e o quadro kanban ponta-a-ponta.
  const [pView, setPView] = useState<'lista' | 'kanban'>('lista')

  const supplierOptions = useMemo(() => {
    const set = new Set<string>()
    rows.forEach(r => { if (r.suppliers?.name) set.add(r.suppliers.name) })
    return Array.from(set).sort()
  }, [rows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(r => {
      if (q && !r.sku.toLowerCase().includes(q) && !r.name.toLowerCase().includes(q)) return false
      if (supplierFilter && r.suppliers?.name !== supplierFilter) return false
      if (statusFilter.size > 0 && !statusFilter.has(r.kanban_status)) return false
      if (priorityFilter.size > 0) {
        if (priorityFilter.has('Zerado') && priorityFilter.size === 1) {
          if (!isEffectiveZerado(r)) return false
        } else if (!priorityFilter.has(r.priority)) return false
      }
      if (encalheOnly && !r.encalhe) return false
      return true
    })
  }, [rows, search, supplierFilter, statusFilter, priorityFilter, encalheOnly])

  const counts = PRIORITY_ORDER.reduce<Record<ReplenishmentPriority, number>>((acc, p) => {
    acc[p] = 0
    return acc
  }, {} as Record<ReplenishmentPriority, number>)
  rows.forEach(r => {
    if (r.priority === 'Zerado') {
      if (!HIDES_ZERADO.has(r.kanban_status)) counts['Zerado']++
    } else {
      counts[r.priority]++
    }
  })

  const statusCounts = STATUS_CHIPS.reduce<Record<string, number>>((acc, s) => {
    acc[s] = 0
    return acc
  }, {})
  rows.forEach(r => {
    if (statusCounts[r.kanban_status] !== undefined) statusCounts[r.kanban_status]++
  })

  const rupturaCount = rows.filter(r => isRupturaAcionavel(r.current_stock, r.kanban_status)).length
  const rupturaPct = rows.length > 0 ? (rupturaCount / rows.length) * 100 : 0

  const encalheCount = rows.filter(r => r.encalhe).length

  function togglePriority(p: ReplenishmentPriority) {
    const next = new Set(priorityFilter)
    if (next.has(p)) next.delete(p); else next.add(p)
    setPriorityFilter(next)
  }

  function toggleStatus(s: KanbanStatusOp) {
    const next = new Set(statusFilter)
    if (next.has(s)) next.delete(s); else next.add(s)
    setStatusFilter(next)
  }

  function clearFilters() {
    setSearch('')
    setSupplierFilter('')
    setStatusFilter(new Set())
    setPriorityFilter(new Set())
    setEncalheOnly(false)
  }

  const hasFilters = search || supplierFilter || statusFilter.size > 0 || priorityFilter.size > 0 || encalheOnly

  // ── Mobile: lista de cards gameficados ────────────────────────────────
  if (isMobile) {
    return (
      <AppLayout title="Painel K">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center text-danger py-16 text-sm">{error}</div>
        ) : (
          <div className="space-y-3">
            <div className="flex bg-sunken border border-line rounded-xl p-1 w-fit">
              {(['lista', 'kanban'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => setPView(v)}
                  className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${pView === v ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted'}`}
                >
                  {v === 'lista' ? '☰ Lista' : '▦ Kanban'}
                </button>
              ))}
            </div>
            {pView === 'lista'
              ? <MobileReplenishmentList rows={rows} supplierOptions={supplierOptions} />
              : <PainelKanban rows={rows} />}
          </div>
        )}
      </AppLayout>
    )
  }

  // ── Desktop: matrix original (inalterada) ─────────────────────────────
  return (
    <AppLayout title="Painel K">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-base-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64 text-danger text-sm">
          Erro ao carregar dados: {error}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Linha única: [prioridade + status] · [Ruptura centro] · [Total] */}
          <div className="grid grid-cols-3 items-center gap-4">
            {/* Coluna esquerda — Prioridade + Status (chips padronizados em 14px) */}
            <div className="flex flex-wrap gap-2 items-center justify-start">
              {PRIORITY_ORDER.filter(p => p !== 'SemFornecedor').map(p => {
                const active = priorityFilter.has(p)
                return (
                  <button
                    key={p}
                    onClick={() => togglePriority(p)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${PRIORITY_BADGE[p]} ${
                      active ? 'ring-2 ring-ink/50 scale-105' : 'opacity-80 hover:opacity-100'
                    }`}
                    title={`Filtrar por ${p}`}
                  >
                    <span>{p}</span>
                    <span className="bg-black/20 rounded-full px-1.5 py-0.5 text-sm tabular-nums">
                      {counts[p]}
                    </span>
                  </button>
                )
              })}
              {STATUS_CHIPS.map(s => {
                const active = statusFilter.has(s)
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${STATUS_BADGE[s] ?? 'bg-gray-700 text-white'} ${
                      active ? 'ring-2 ring-ink/50 scale-105' : 'opacity-80 hover:opacity-100'
                    }`}
                    title={`Filtrar por ${s}`}
                  >
                    <span>{s}</span>
                    <span className="bg-black/20 rounded-full px-1.5 py-0.5 text-sm tabular-nums">
                      {statusCounts[s]}
                    </span>
                  </button>
                )
              })}
              <button
                onClick={() => setEncalheOnly(v => !v)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all bg-warn/15 text-[#9a5a28] border border-warn/45 dark:bg-warn/20 dark:text-amber-300 ${
                  encalheOnly ? 'ring-2 ring-ink/50 scale-105' : 'opacity-80 hover:opacity-100'
                }`}
                title="Filtrar itens encalhados (com saldo, sem venda há +90 dias)"
              >
                <span>⚠ Encalhe</span>
                <span className="bg-black/20 rounded-full px-1.5 py-0.5 text-sm tabular-nums">
                  {encalheCount}
                </span>
              </button>
            </div>

            {/* Coluna central — Ruptura (destaque) */}
            <div className="flex justify-center">
              <button
                onClick={() => togglePriority('Zerado')}
                title={`${rupturaCount} de ${rows.length} SKUs ativos com saldo zerado · Clique para filtrar`}
                className={`px-6 py-3 rounded-lg font-semibold border transition-all text-[36px] leading-none whitespace-nowrap ${
                  rupturaPct > 10
                    ? 'bg-danger/10 text-danger border-danger/40 hover:bg-danger/20'
                    : 'bg-monitor/15 text-[#7a5e16] border-monitor/40 hover:bg-monitor/25 dark:text-amber-300'
                } ${priorityFilter.has('Zerado') ? 'ring-2 ring-ink/50 scale-105' : ''}`}
              >
                Ruptura: {rupturaPct.toFixed(1).replace('.', ',')}%
              </button>
            </div>

            {/* Coluna direita — Total */}
            <div className="flex justify-end">
              <div className="px-3 py-1.5 rounded-lg text-sm font-medium bg-sunken text-ink-muted border border-line whitespace-nowrap">
                {filtered.length === rows.length ? `Total: ${rows.length}` : `${filtered.length} de ${rows.length}`}
              </div>
            </div>
          </div>

          {/* Busca + fornecedor */}
          <div className="bg-surface border border-line rounded-xl p-3 flex flex-wrap gap-3 items-center">
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar SKU ou produto..."
              className="flex-1 min-w-0 sm:min-w-[220px] bg-paper border border-line rounded px-3 py-1.5 text-ink text-sm placeholder:text-ink-faint"
            />

            <select
              value={supplierFilter}
              onChange={e => setSupplierFilter(e.target.value)}
              className="bg-paper border border-line rounded px-3 py-1.5 text-ink text-sm w-full sm:w-auto sm:min-w-[200px]"
            >
              <option value="">Todos os fornecedores</option>
              {supplierOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="text-xs px-3 py-1.5 bg-sunken hover:bg-line text-ink-muted rounded border border-line"
              >
                Limpar filtros
              </button>
            )}
          </div>

          <ReplenishmentMatrix rows={filtered} hasFiliais={hasFiliais} filialMap={filialMap} />
        </div>
      )}
    </AppLayout>
  )
}
